import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { XCircle, UserCheck, Clock, RotateCcw, Loader2, CalendarX, CheckCircle2, Flag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "@/hooks/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useListProjects } from "@workspace/api-client-react";

const API_BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

interface DecisionEntry {
  id: string;
  projectId: string | null;
  taskId: string | null;
  taskTitle: string | null;
  actionType: string;
  fromValue: string | null;
  toValue: string | null;
  reason: string | null;
  createdAt: string;
  currentTaskStatus: string | null;
}

const ACTION_META: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
  task_refused: {
    label: "Refusée",
    icon: <XCircle className="w-4 h-4" />,
    color: "text-red-500",
  },
  task_delegated: {
    label: "Déléguée",
    icon: <UserCheck className="w-4 h-4" />,
    color: "text-blue-500",
  },
  task_postponed: {
    label: "Reportée",
    icon: <Clock className="w-4 h-4" />,
    color: "text-amber-500",
  },
  task_done: {
    label: "Terminée",
    icon: <CheckCircle2 className="w-4 h-4" />,
    color: "text-emerald-500",
  },
  task_reactivated: {
    label: "Réactivée",
    icon: <RotateCcw className="w-4 h-4" />,
    color: "text-violet-500",
  },
  priority_changed: {
    label: "Priorité modifiée",
    icon: <Flag className="w-4 h-4" />,
    color: "text-orange-500",
  },
};

function actionMeta(actionType: string) {
  return ACTION_META[actionType] ?? {
    label: actionType,
    icon: <CalendarX className="w-4 h-4" />,
    color: "text-muted-foreground",
  };
}

function groupByDay(entries: DecisionEntry[]) {
  const groups: { date: string; items: DecisionEntry[] }[] = [];
  for (const entry of entries) {
    const day = entry.createdAt.slice(0, 10);
    const last = groups[groups.length - 1];
    if (last && last.date === day) {
      last.items.push(entry);
    } else {
      groups.push({ date: day, items: [entry] });
    }
  }
  return groups;
}

function formatDay(dateStr: string): string {
  const d = new Date(dateStr + "T12:00:00");
  return format(d, "EEEE d MMMM yyyy", { locale: fr });
}

export default function DecisionsPage() {
  const qc = useQueryClient();
  const [projectFilter, setProjectFilter] = useState<string>("all");

  const { data: projects = [] } = useListProjects();

  const apiUrl = projectFilter !== "all"
    ? `${API_BASE}/api/projects/${projectFilter}/decision-log`
    : `${API_BASE}/api/decision-log`;

  const { data: entries = [], isLoading } = useQuery<DecisionEntry[]>({
    queryKey: ["decision-log", projectFilter],
    queryFn: async () => {
      const res = await fetch(apiUrl, { credentials: "include" });
      if (!res.ok) throw new Error("failed");
      return res.json() as Promise<DecisionEntry[]>;
    },
  });

  const reactivate = useMutation({
    mutationFn: async (taskId: string) => {
      const res = await fetch(`${API_BASE}/api/tasks/${taskId}/reactivate`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) throw new Error("failed");
      return res.json();
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["decision-log"] });
      void qc.invalidateQueries({ queryKey: ["tasks"] });
      toast({ title: "Tâche réactivée", description: "Elle a été remise en todo." });
    },
    onError: () => {
      toast({ title: "Erreur", description: "Impossible de réactiver la tâche.", variant: "destructive" });
    },
  });

  const reactivatable = entries.filter(
    (e) =>
      e.taskId !== null &&
      (e.actionType === "task_refused" || e.actionType === "task_delegated" || e.actionType === "task_postponed") &&
      (e.currentTaskStatus === "refused" || e.currentTaskStatus === "delegated" || e.currentTaskStatus === "postponed"),
  );

  const allGroups = groupByDay(entries);

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 space-y-8">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Journal des décisions</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Retrouvez ici vos tâches refusées, déléguées, reportées et complétées.
          </p>
        </div>

        <Select value={projectFilter} onValueChange={setProjectFilter}>
          <SelectTrigger className="w-48 shrink-0">
            <SelectValue placeholder="Tous les projets" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les projets</SelectItem>
            {projects.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {reactivatable.length > 0 && (
        <section>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">
            À réactiver ({reactivatable.length})
          </h2>
          <div className="space-y-2">
            {reactivatable.map((entry) => {
              const meta = actionMeta(entry.actionType);
              return (
                <Card key={entry.id} className="rounded-xl">
                  <CardContent className="p-4 flex items-center gap-3">
                    <span className={meta.color}>{meta.icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">
                        {entry.taskTitle ?? "(tâche supprimée)"}
                      </div>
                      {entry.reason && (
                        <div className="text-xs text-muted-foreground truncate mt-0.5">
                          {entry.reason}
                        </div>
                      )}
                      {entry.toValue && entry.actionType === "task_delegated" && (
                        <div className="text-xs text-muted-foreground mt-0.5">
                          → {entry.toValue}
                        </div>
                      )}
                      {entry.toValue && entry.actionType === "task_postponed" && (
                        <div className="text-xs text-muted-foreground mt-0.5">
                          Reportée au {entry.toValue}
                        </div>
                      )}
                    </div>
                    <Badge variant="secondary" className="shrink-0 text-xs">
                      {meta.label}
                    </Badge>
                    {entry.taskId && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="shrink-0"
                        disabled={reactivate.isPending}
                        onClick={() => reactivate.mutate(entry.taskId!)}
                      >
                        {reactivate.isPending ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <RotateCcw className="w-3 h-3 mr-1" />
                        )}
                        Réactiver
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>
      )}

      <section>
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">
          Chronologie complète
        </h2>
        {isLoading ? (
          <div className="flex items-center gap-2 text-muted-foreground text-sm py-8">
            <Loader2 className="w-4 h-4 animate-spin" />
            Chargement…
          </div>
        ) : entries.length === 0 ? (
          <p className="text-muted-foreground text-sm py-8 text-center">
            Aucune décision enregistrée pour l'instant. Commencez par terminer, refuser ou déléguer une tâche.
          </p>
        ) : (
          <div className="relative">
            <div className="absolute left-[7px] top-2 bottom-2 w-px bg-border/60" />
            <div className="space-y-6 pl-6">
              {allGroups.map((group) => (
                <div key={group.date}>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 capitalize">
                    {formatDay(group.date)}
                  </p>
                  <div className="space-y-3">
                    {group.items.map((entry) => {
                      const meta = actionMeta(entry.actionType);
                      return (
                        <div key={entry.id} className="relative flex items-start gap-2">
                          <span
                            className="absolute -left-6 top-1 w-3.5 h-3.5 rounded-full bg-background border-2 border-border"
                            style={{ transform: "translateX(-1px)" }}
                          />
                          <span className={`shrink-0 mt-0.5 ${meta.color}`}>{meta.icon}</span>
                          <div className="flex-1 min-w-0">
                            <span className="text-sm font-medium">
                              {entry.taskTitle ?? "(tâche supprimée)"}
                            </span>
                            <Badge variant="outline" className="ml-2 text-xs py-0">
                              {meta.label}
                            </Badge>
                            {entry.toValue && entry.actionType === "task_delegated" && (
                              <span className="text-xs text-muted-foreground ml-2">→ {entry.toValue}</span>
                            )}
                            {entry.toValue && entry.actionType === "task_postponed" && (
                              <span className="text-xs text-muted-foreground ml-2">→ {entry.toValue}</span>
                            )}
                            {entry.fromValue && entry.toValue && entry.actionType === "priority_changed" && (
                              <span className="text-xs text-muted-foreground ml-2">{entry.fromValue} → {entry.toValue}</span>
                            )}
                            {entry.reason && (
                              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                                {entry.reason}
                              </p>
                            )}
                            <p className="text-xs text-muted-foreground/60 mt-0.5">
                              {format(new Date(entry.createdAt), "HH:mm", { locale: fr })}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
